import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-none uppercase transition-colors outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'border border-transparent bg-brand-red text-white hover:bg-brand-darkRed',
        secondaryDark: 'border border-charcoal-600 text-white hover:bg-charcoal-800',
        secondaryLight:
          'border border-gray-300 bg-white text-charcoal-900 hover:bg-gray-50 hover:border-charcoal-900',
        outlineStrong:
          'border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white',
        charcoal:
          'border border-transparent bg-charcoal-900 text-white hover:bg-charcoal-800',
        mockupToggle:
          'rounded-lg px-6 py-2.5 text-sm font-bold transition-all active:scale-95',
        iconGhost:
          'border-gray-200 bg-white/40 text-charcoal-900 hover:bg-white normal-case rounded-none',
        iconFloating:
          'h-10 w-10 rounded-full border border-gray-200 bg-white text-charcoal-900 shadow-md normal-case hover:border-charcoal-400 hover:shadow-lg',
      },
      size: {
        xs: 'px-4 py-1.5 text-fs-sm font-bold tracking-widest',
        sm: 'px-6 py-3 text-fs-xl font-semibold tracking-wide',
        md: 'px-8 py-4 text-sm font-semibold tracking-wide',
        lg: 'px-10 py-4 text-fs-xl font-bold tracking-wide',
        strip: 'px-8 py-3 text-sm font-bold tracking-wide',
        mockupToggle: 'normal-case',
        icon: 'p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { buttonVariants }

